/*
  DRIEW quick add — add to cart straight from a product card.

  A thin "Add to cart" button sits under the price, on desktop and phones alike. Single-variant pieces are added straight away; multi-variant ones open a size
  sheet (bottom sheet on phones, small dialog on desktop).

  Markup: snippets/driew-quick-add.liquid (Dawn cards) and the JS-rendered cards
  in blocks/Infinite_Scroll.liquid. The cart is the "page" type (no drawer), so
  after adding we update the header count and show a small toast.
*/
(() => {
  if (window.DriewQuickAdd) return;
  window.DriewQuickAdd = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // The theme's "show currency codes" setting (layout/theme.liquid), so the size
  // sheet reads "$692.00 USD" like the cards around it.
  const SHOW_CURRENCY_CODE = !!(document.currentScript && document.currentScript.dataset.currencyCode === 'true');
  const T = {
    addToCart: (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart',
    adding: 'Adding…',
    added: 'Added',
    addedToCart: 'Added to cart',
    viewCart: 'View cart',
    viewDetails: 'View full details',
    inCart: 'Already in your cart',
    soldOut: 'Sold out',
    failed: 'Couldn’t add — try again',
    chooseSize: 'Choose a size',
    chooseOption: 'Choose an option',
    close: 'Close',
  };
  const ICON_CLOSE =
    '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1.5 1.5l9 9m0-9l-9 9" stroke="currentColor" stroke-width="1.1" fill="none"/></svg>';

  const esc = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );

  const sized = (url, width) => (url ? url.split('?')[0] + '?width=' + width : '');

  // In the currency the prices' own response reports (the size sheet's data
  // view says so); otherwise the page's.
  const money = (cents, reported) => {
    const currency =
      reported || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    let text;
    try {
      text = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(
        cents / 100
      );
    } catch (e) {
      text = '$' + (cents / 100).toFixed(2);
    }
    return SHOW_CURRENCY_CODE ? `${text} ${currency}` : text;
  };

  // ---- Cart ---------------------------------------------------------------

  function friendlyError(data) {
    const message = String((data && (data.description || data.message)) || '');
    if (/sold out|unavailable|not available/i.test(message)) return T.soldOut;
    if (/in your cart|can.t add more|already/i.test(message)) return T.inCart;
    return T.failed;
  }

  function updateCartCount(count) {
    document.querySelectorAll('#cart-icon-bubble').forEach((link) => {
      let bubble = link.querySelector('.cart-count-bubble');
      if (!count) {
        if (bubble) bubble.remove();
        return;
      }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        link.appendChild(bubble);
      }
      bubble.innerHTML =
        (count < 100 ? `<span aria-hidden="true">${count}</span>` : '') +
        `<span class="visually-hidden">${count} ${count === 1 ? 'item' : 'items'}</span>`;
    });
  }

  async function addVariant(variantId) {
    const response = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(friendlyError(data));
    }
    const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    updateCartCount(cart.item_count);
    // Let Dawn's cart components (cart page) refresh too.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'driew-quick-add', productVariantId: variantId, cartData: cart });
    }
    return cart;
  }

  // ---- Card button states -------------------------------------------------

  function setCardState(el, state, message) {
    const label = el.querySelector('.dqa__label');
    if (label && el.dataset.dqaIdle === undefined) el.dataset.dqaIdle = label.textContent;
    clearTimeout(el.dqaTimer);
    el.classList.remove('is-loading', 'is-added', 'is-error');
    el.removeAttribute('aria-busy');
    if (state !== 'idle') el.classList.add('is-' + state);
    if (state === 'loading') el.setAttribute('aria-busy', 'true');
    if (label) {
      label.textContent =
        state === 'loading'
          ? message || T.adding
          : state === 'added'
          ? T.added
          : state === 'error'
          ? message
          : el.dataset.dqaIdle;
    }
    if (state === 'added' || state === 'error') el.dqaTimer = setTimeout(() => setCardState(el, 'idle'), 2600);
  }

  async function quickAdd(el) {
    setCardState(el, 'loading');
    try {
      await addVariant(el.dataset.dqaVariant);
      setCardState(el, 'added');
      showToast({ title: el.dataset.dqaTitle, image: el.dataset.dqaImage });
    } catch (error) {
      setCardState(el, 'error', error.message);
      showToast({ title: el.dataset.dqaTitle, error: error.message });
    }
  }

  // Capture phase: cards are links (or sit under Dawn's full-card link), so the
  // click must be claimed before the browser follows it.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest && event.target.closest('[data-dqa-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      const el = trigger.closest('[data-dqa]');
      if (!el || el.classList.contains('is-loading')) return;
      if (el.hasAttribute('data-dqa-multi')) openSheet(el);
      else quickAdd(el);
    },
    true
  );

  // ---- Size sheet (multi-variant products) -------------------------------

  let sheet;
  let sheetSource;

  function getSheet() {
    if (sheet) return sheet;
    sheet = document.createElement('dialog');
    sheet.className = 'dqa-sheet';
    sheet.setAttribute('aria-labelledby', 'DqaSheetTitle');
    sheet.innerHTML = `
      <div class="dqa-sheet__inner">
        <div class="dqa-sheet__head">
          <img class="dqa-sheet__img" alt="" width="72" height="96">
          <div class="dqa-sheet__meta">
            <p class="dqa-sheet__vendor"></p>
            <h2 class="dqa-sheet__title" id="DqaSheetTitle"></h2>
            <p class="dqa-sheet__price"></p>
          </div>
          <button type="button" class="dqa-sheet__close" data-dqa-close aria-label="${T.close}">${ICON_CLOSE}</button>
        </div>
        <fieldset class="dqa-sheet__options">
          <legend class="dqa-sheet__legend"></legend>
          <div class="dqa-sheet__chips"></div>
        </fieldset>
        <button type="button" class="dqa-sheet__add" data-dqa-sheet-add disabled></button>
        <a class="dqa-sheet__more" href="#">${T.viewDetails}</a>
      </div>`;
    document.body.appendChild(sheet);

    sheet.addEventListener('click', (event) => {
      // Clicks on the backdrop land on the <dialog> itself.
      if (event.target === sheet || event.target.closest('[data-dqa-close]')) sheet.close();
    });
    sheet.addEventListener('close', () => document.documentElement.classList.remove('dqa-lock'));
    sheet.addEventListener('change', (event) => {
      if (event.target.name === 'dqa-variant') syncSheetButton();
    });
    sheet.querySelector('[data-dqa-sheet-add]').addEventListener('click', addFromSheet);
    return sheet;
  }

  function syncSheetButton() {
    const button = sheet.querySelector('[data-dqa-sheet-add]');
    const checked = sheet.querySelector('input[name="dqa-variant"]:checked');
    const anyAvailable = sheet.querySelector('input[name="dqa-variant"]:not(:disabled)');
    button.disabled = !checked;
    button.textContent = checked ? T.addToCart : anyAvailable ? sheetSource.chooseLabel : T.soldOut;
  }

  async function openSheet(el) {
    setCardState(el, 'loading', '…');
    let product;
    try {
      // The theme's data view (templates/product.quick-add-data.liquid) rather
      // than /products/<handle>.js: it says which currency its prices are in.
      // No "Accept: application/json" here: with it, Shopify answers with its
      // own product JSON instead of the view.
      const response = await fetch(
        ROOT + 'products/' + encodeURIComponent(el.dataset.dqaHandle) + '?view=quick-add-data'
      );
      if (!response.ok) throw new Error(String(response.status));
      product = await response.json();
      if (!product || !Array.isArray(product.variants)) throw new Error('Unexpected product data');
    } catch (error) {
      window.location.href = ROOT + 'products/' + el.dataset.dqaHandle;
      return;
    }
    setCardState(el, 'idle');

    const s = getSheet();
    // Older Safari (< 15.4) has no <dialog>: pick the size on the product page.
    if (typeof s.showModal !== 'function') {
      window.location.href = product.url || ROOT + 'products/' + product.handle;
      return;
    }
    const options = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const isSize = options.length === 1 && /size/i.test(options[0]);
    const available = product.variants.filter((v) => v.available);
    const prices = (available.length ? available : product.variants).map((v) => v.price);
    const minPrice = Math.min(...prices);
    sheetSource = { el, product, chooseLabel: isSize ? T.chooseSize : T.chooseOption };

    const img = s.querySelector('.dqa-sheet__img');
    img.src = sized(product.featured_image, 240);
    img.hidden = !product.featured_image;
    s.querySelector('.dqa-sheet__vendor').textContent = product.vendor || '';
    s.querySelector('.dqa-sheet__title').textContent = product.title;
    s.querySelector('.dqa-sheet__price').textContent =
      (Math.max(...prices) !== minPrice ? 'From ' : '') + money(minPrice, product.currency);
    s.querySelector('.dqa-sheet__legend').textContent = options.length === 1 ? options[0] : 'Options';
    s.querySelector('.dqa-sheet__chips').innerHTML = product.variants
      .map(
        (v) => `
          <label class="dqa-chip${v.available ? '' : ' is-unavailable'}">
            <input type="radio" name="dqa-variant" value="${v.id}" data-title="${esc(v.title)}"
              ${v.available ? '' : 'disabled'}${available.length === 1 && v.available ? ' checked' : ''}>
            <span>${esc(v.title)}</span>
          </label>`
      )
      .join('');
    s.querySelector('.dqa-sheet__more').href = product.url || ROOT + 'products/' + product.handle;
    syncSheetButton();

    document.documentElement.classList.add('dqa-lock');
    s.showModal();
  }

  async function addFromSheet() {
    const button = sheet.querySelector('[data-dqa-sheet-add]');
    const checked = sheet.querySelector('input[name="dqa-variant"]:checked');
    if (!checked || !sheetSource) return;
    const { el, product } = sheetSource;
    const variant = product.variants.find((v) => String(v.id) === checked.value) || {};
    button.disabled = true;
    button.textContent = T.adding;
    try {
      await addVariant(checked.value);
      sheet.close();
      setCardState(el, 'added');
      showToast({
        title: product.title,
        variant: checked.dataset.title,
        image: sized((variant.featured_image && variant.featured_image.src) || product.featured_image, 160),
      });
    } catch (error) {
      button.textContent = error.message;
      setTimeout(() => sheet.open && syncSheetButton(), 2400);
    }
  }

  // ---- Toast ----------------------------------------------------------------

  let toast;
  let toastTimer;

  function hideToastLater() {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4500);
  }

  function showToast({ title, variant, image, error }) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dqa-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      // The theme's pageFadeIn animation leaves a filter on <body>, which makes
      // <body> the containing block for position:fixed — mount on <html> so the
      // toast stays pinned to the viewport (same as the email popup).
      document.documentElement.appendChild(toast);
      toast.addEventListener('mouseenter', () => clearTimeout(toastTimer));
      toast.addEventListener('mouseleave', hideToastLater);
    }
    const name = esc(title) + (variant && variant !== 'Default Title' ? ' — ' + esc(variant) : '');
    const cartLink = !error || error === T.inCart;
    toast.classList.toggle('is-error', !!error);
    toast.innerHTML = `
      ${image && !error ? `<img class="dqa-toast__img" src="${esc(image)}" alt="" width="48" height="64">` : ''}
      <div class="dqa-toast__text">
        <p class="dqa-toast__eyebrow">${esc(error || T.addedToCart)}</p>
        <p class="dqa-toast__title">${name}</p>
      </div>
      ${cartLink ? `<a class="dqa-toast__link" href="${ROOT}cart">${T.viewCart}</a>` : ''}`;
    toast.classList.add('is-visible');
    hideToastLater();
  }
})();
